import { useEffect, useRef, useState } from "react";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "react-oidc-context";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { ContextSelector } from "@/components/ContextSelector";
import { CondicionesUsoModal } from "@/components/CondicionesUsoModal";
import { ThemeToggle } from "@/components/ui";
import { useRolesEfectivos } from "@/lib/sesion";
import { BandaSuplantacion } from "@/components/BandaSuplantacion";
import { BandaAmbiente } from "@/components/BandaAmbiente";
import { SIGLA_AMBIENTE, ETIQUETA_AMBIENTE } from "@/lib/ambiente";
import {
  LogOut,
  Bell,
  HelpCircle,
  User as UserIcon,
  Home,
  ShieldCheck,
  Shield,
  Cpu,
  Settings,
  FlaskConical,
  ClipboardCheck,
  Users,
  UsersRound,
  Inbox,
  ChevronLeft,
  ChevronRight,
  Menu,
  X,
  Zap,
  CircuitBoard,
  Leaf,
} from "lucide-react";
// HelpCircle: solo en el botón del header (el item del sidebar se quitó por duplicado).

// Sectores aún sin módulo: se muestran deshabilitados ("Próximamente").
const SECTORES_FUTUROS = [
  { label: "Electrónico", icon: CircuitBoard },
  { label: "Eléctrica", icon: Zap },
  { label: "Eficiencia energética", icon: Leaf },
] as const;

const SIDEBAR_COLLAPSED_KEY = "sicen.sidebar.collapsed";

// Conjuntos de roles para condicionar la navegación.
const ROLES_CALIDAD = ["GERENTE_CALIDAD", "AUXILIAR_CALIDAD", "ADMIN_SICEN"];
const ROLES_CLIENTE_RL = ["TITULAR", "TRAMITADOR", "USUARIO_GESTOR", "REPRESENTANTE_LEGAL", "ADMIN_SICEN"];
const ROLES_TELECOM = [
  "RESPONSABLE_TELECOM",
  "EVALUADOR_TECNICO",
  "COORDINADOR_TECNICO",
  "AUDITOR_INTERNO",
  "TRAMITADOR",
  "TITULAR",
  "USUARIO_GESTOR",
  "REPRESENTANTE_LEGAL",
  "FACTURACION",
  "ADMIN_SICEN",
];
const ROLES_ADMIN = ["ADMIN_SICEN"];
// Internos del OC: ven la bandeja «Mis solicitudes asignadas» (RF-03).
const ROLES_INTERNOS_OC = [
  "ADMIN_SICEN", "GERENTE_CALIDAD", "AUXILIAR_CALIDAD", "COORDINADOR_TECNICO",
  "EVALUADOR_TECNICO", "RESPONSABLE_TELECOM", "RESPONSABLE_ELECTRICA",
  "RESPONSABLE_EFICIENCIA", "RESPONSABLE_CERTIFICADOS", "FACTURACION",
  "AUDITOR_INTERNO", "DIRECCION", "VIGILANCIA",
];

function hasAnyRole(userRoleList: string[], wanted: string[]): boolean {
  return wanted.some((r) => userRoleList.includes(r));
}

export function AppLayout() {
  const auth = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const profile = auth.user?.profile;

  // Colapsado: preferencia de escritorio persistida. Por defecto arranca
  // colapsado (doc UX 20260627) para dar protagonismo al área de trabajo;
  // sólo respeta una preferencia explícita previa del usuario.
  const [collapsed, setCollapsed] = useState(
    () => localStorage.getItem(SIDEBAR_COLLAPSED_KEY) !== "0"
  );
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    localStorage.setItem(SIDEBAR_COLLAPSED_KEY, collapsed ? "1" : "0");
  }, [collapsed]);

  // Al navegar, cerrar el drawer móvil (no afecta el colapsado de escritorio).
  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);
  const displayName = profile?.name ?? profile?.preferred_username ?? "Usuario";
  const roles = useRolesEfectivos();
  // Telecom y Seguridad comparten roles operativos; un solo gate cubre ambos.
  const puedeVerSectores = hasAnyRole(roles, ROLES_TELECOM);

  const opts = {
    enabled: puedeVerSectores,
    refetchInterval: 60 * 1000,
    refetchOnWindowFocus: true,
    staleTime: 30 * 1000,
  } as const;
  const qContadoresTelecom = useQuery<{ enProceso: number }>({
    queryKey: ["sicen-contadores-telecom"],
    queryFn: async () =>
      (await api.get<{ enProceso: number }>("/api/sector/telecom/solicitudes/contadores")).data,
    ...opts,
  });
  const qContadoresSeguridad = useQuery<{ enProceso: number }>({
    queryKey: ["sicen-contadores-seguridad"],
    queryFn: async () =>
      (await api.get<{ enProceso: number }>("/api/sector/seguridad/solicitudes/contadores")).data,
    ...opts,
  });
  // Total agregado en proceso a través de los sectores con módulo.
  const enProceso = (qContadoresTelecom.data?.enProceso ?? 0) + (qContadoresSeguridad.data?.enProceso ?? 0);
  const puedeVerTelecom = puedeVerSectores;

  return (
    <div className="flex min-h-screen">
      {/* Backdrop del drawer móvil */}
      {mobileOpen && (
        <div
          className="fixed inset-0 z-30 bg-black/40 lg:hidden"
          aria-hidden="true"
          onClick={() => setMobileOpen(false)}
        />
      )}

      <aside
        className={`fixed inset-y-0 left-0 z-40 flex w-60 flex-col border-r border-edge bg-surface transition-transform duration-200 lg:relative lg:z-auto lg:translate-x-0 lg:transition-[width] ${
          mobileOpen ? "translate-x-0" : "-translate-x-full"
        } ${collapsed ? "lg:w-16" : "lg:w-60"}`}
      >
        {/* Toggle flotante en el borde derecho (solo escritorio) */}
        <button
          type="button"
          aria-label={collapsed ? "Expandir menú" : "Colapsar menú"}
          aria-expanded={!collapsed}
          title={collapsed ? "Expandir menú" : "Colapsar menú"}
          onClick={() => setCollapsed((v) => !v)}
          className="absolute -right-3 top-20 z-50 hidden h-6 w-6 cursor-pointer place-items-center rounded-full border border-edge bg-surface text-fg-subtle shadow-sm transition hover:border-primary-strong hover:text-primary-strong hover:shadow lg:grid"
        >
          {collapsed ? <ChevronRight className="h-4 w-4" /> : <ChevronLeft className="h-4 w-4" />}
        </button>
        <div className={`flex items-center gap-3 border-b border-edge px-4 py-3 ${collapsed ? "lg:justify-center lg:px-0" : ""}`}>
          <img
            src="/assets/logo-NPMX.png"
            alt="NPMX"
            className="h-9 w-9 shrink-0 object-contain"
          />
          <div className={`leading-tight ${collapsed ? "lg:hidden" : ""}`}>
            <div className="flex items-center gap-1.5 text-sm font-semibold text-fg">
              SICEN
              {SIGLA_AMBIENTE && (
                <span
                  title={ETIQUETA_AMBIENTE ?? undefined}
                  className="rounded bg-amber-400 px-1.5 py-px text-[10px] font-bold uppercase tracking-wide text-amber-950"
                >
                  {SIGLA_AMBIENTE}
                </span>
              )}
            </div>
            <div className="text-xs text-fg-subtle">NPMX — Certificaciones</div>
          </div>
          {/* Cerrar drawer en móvil */}
          <button
            className="ml-auto rounded p-1 text-fg-subtle hover:bg-surface-2 lg:hidden"
            aria-label="Cerrar menú"
            onClick={() => setMobileOpen(false)}
          >
            <X className="h-5 w-5" />
          </button>
        </div>
        <nav className="flex-1 space-y-0.5 px-2 py-3 text-sm">
          <NavItem to="/" icon={<Home className="h-4 w-4" />} label="Inicio" collapsed={collapsed} end />
          {hasAnyRole(roles, ROLES_INTERNOS_OC) && (
            <NavItem to="/mis-asignadas" icon={<Inbox className="h-4 w-4" />} label="Mis asignadas" collapsed={collapsed} />
          )}
          {hasAnyRole(roles, ROLES_CLIENTE_RL) && (
            <NavItem to="/mi-registro-legal" icon={<ClipboardCheck className="h-4 w-4" />} label="Mi Registro Legal" collapsed={collapsed} />
          )}
          {hasAnyRole(roles, ROLES_CALIDAD) && (
            <NavItem to="/tramites-legales" icon={<ShieldCheck className="h-4 w-4" />} label="Trámites Legales" collapsed={collapsed} />
          )}
          {hasAnyRole(roles, ROLES_TELECOM) && (
            <NavItem to="/telecom" icon={<Cpu className="h-4 w-4" />} label="Telecomunicaciones" collapsed={collapsed} />
          )}
          {hasAnyRole(roles, ROLES_TELECOM) && (
            <NavItem to="/seguridad" icon={<Shield className="h-4 w-4" />} label="Seguridad" collapsed={collapsed} />
          )}
          {hasAnyRole(roles, ROLES_ADMIN) && (
            <NavItem to="/admin/laboratorios" icon={<FlaskConical className="h-4 w-4" />} label="Laboratorios" collapsed={collapsed} />
          )}
          {/* D-04/IAM 2026-07-16: Calidad gestiona usuarios operativos (estatus,
              reset, altas); las cuentas admin siguen blindadas en el backend. */}
          {hasAnyRole(roles, ROLES_CALIDAD) && (
            <NavItem to="/admin/usuarios" icon={<Users className="h-4 w-4" />} label="Usuarios" collapsed={collapsed} />
          )}
          {hasAnyRole(roles, ROLES_ADMIN) && (
            <NavItem to="/admin/responsables-etapa" icon={<UsersRound className="h-4 w-4" />} label="Responsables por etapa" collapsed={collapsed} />
          )}
          {hasAnyRole(roles, ROLES_ADMIN) && (
            <NavItem to="/admin/plantillas" icon={<Settings className="h-4 w-4" />} label="Administración" collapsed={collapsed} />
          )}

          {/* Sectores futuros: visibles pero deshabilitados ("Próximamente").
              Compactados (doc UX 20260627): separador tenue + items densos. */}
          <div className={`mt-4 border-t border-edge/60 pt-3 ${collapsed ? "lg:hidden" : ""}`}>
            <div className="px-3 pb-1 text-[10px] font-semibold uppercase tracking-wide text-fg-faint">
              Próximamente
            </div>
            {SECTORES_FUTUROS.map((s) => (
              <NavItemDisabled key={s.label} icon={<s.icon className="h-4 w-4" />} label={s.label} collapsed={collapsed} />
            ))}
          </div>
        </nav>
        <div className={`border-t border-edge px-3 py-3 text-xs text-fg-subtle ${collapsed ? "lg:hidden" : ""}`}>
          {roles.length > 0 ? (
            <span>{roles.filter((r) => !r.startsWith("default") && !r.startsWith("offline") && !r.startsWith("uma")).join(" · ") || "Sin roles"}</span>
          ) : (
            <span>Sin roles</span>
          )}
        </div>
      </aside>

      <div className="flex min-w-0 flex-1 flex-col">
        {/* Banda de ambiente (solo fuera de producción) y header viajan juntos
            pegados arriba: la banda no debe desaparecer al hacer scroll. */}
        <div className="sticky top-0 z-20">
          <BandaAmbiente />
          {/* El header conserva el navy institucional en ambos temas; en dark
              baja a npmx-950 para no competir con las superficies. */}
          <header className="bg-npmx-700 text-white shadow dark:border-b dark:border-edge dark:bg-npmx-950">
            <div className="flex items-center justify-between gap-4 px-4 py-2 sm:px-6">
              <button
                className="rounded p-2 hover:bg-white/10 lg:hidden"
                aria-label="Abrir menú"
                onClick={() => setMobileOpen(true)}
              >
                <Menu className="h-5 w-5" />
              </button>
              {/* Escritorio: selector de contexto centrado en la barra. En móvil
                  va en su propia fila (abajo) para no encimarse con los iconos. */}
              <div className="hidden min-w-0 flex-1 justify-center lg:flex">
                <ContextSelector />
              </div>
              <div className="ml-auto flex items-center gap-3 lg:ml-0">
                <CampanaPendientes
                  enabled={puedeVerTelecom}
                  total={enProceso}
                  porSector={[
                    { codigo: "telecom", label: "Telecomunicaciones", n: qContadoresTelecom.data?.enProceso ?? 0 },
                    { codigo: "seguridad", label: "Seguridad", n: qContadoresSeguridad.data?.enProceso ?? 0 },
                  ]}
                  onIr={(codigo) => navigate(`/${codigo}?filtro=en-proceso`)}
                />
                <ThemeToggle className="hover:bg-white/10" />
                <button
                  className="rounded p-2 hover:bg-white/10"
                  aria-label="Centro de Ayuda"
                  title="Centro de Ayuda"
                  onClick={() => navigate("/ayuda")}
                >
                  <HelpCircle className="h-5 w-5" />
                </button>
                <div className="flex items-center gap-2 border-l border-white/20 pl-3">
                  <div className="grid h-8 w-8 place-items-center rounded-full bg-white/20">
                    <UserIcon className="h-4 w-4" />
                  </div>
                  <div className="hidden text-xs leading-tight sm:block">
                    <div className="font-medium">{displayName}</div>
                    <div className="text-white/60">{profile?.email}</div>
                  </div>
                  <button
                    className="ml-2 rounded p-2 hover:bg-white/10"
                    aria-label="Cerrar sesión"
                    onClick={() => auth.signoutRedirect()}
                  >
                    <LogOut className="h-5 w-5" />
                  </button>
                </div>
              </div>
            </div>
            {/* Móvil: selector de contexto en fila propia, ancho completo. */}
            <div className="border-t border-white/10 px-4 pb-2 pt-1 lg:hidden">
              <ContextSelector />
            </div>
          </header>
        </div>

        <BandaSuplantacion />

        <main className="flex-1 overflow-x-hidden px-4 py-5 sm:px-6 sm:py-7">
          <Outlet />
        </main>

        <footer className="px-4 py-2.5 text-center text-[11px] text-fg-faint sm:px-6">
          SICEN © 2026 — NPMX S.C. · Acreditación EMA 130/17 · Autorización IFT/223/UCS/7706/2025
        </footer>
      </div>

      <CondicionesUsoModal />
    </div>
  );
}

function NavItem({
  to,
  icon,
  label,
  collapsed,
  end,
}: {
  to: string;
  icon: React.ReactNode;
  label: string;
  collapsed?: boolean;
  end?: boolean;
}) {
  return (
    <NavLink
      to={to}
      end={end}
      title={collapsed ? label : undefined}
      className={({ isActive }) =>
        `relative flex items-center gap-2.5 rounded-lg px-3 py-2 transition ${
          collapsed ? "lg:justify-center lg:px-2" : ""
        } ${
          isActive
            ? "bg-primary-tint font-semibold text-primary-strong before:absolute before:inset-y-1.5 before:left-0 before:w-1 before:rounded-r before:bg-primary-strong"
            : "text-fg-muted hover:bg-surface-2 hover:text-fg"
        }`
      }
    >
      {icon}
      <span className={collapsed ? "lg:hidden" : ""}>{label}</span>
    </NavLink>
  );
}

/** Sector futuro: visible pero sin navegación, marcado como "Próximamente". */
function NavItemDisabled({
  icon,
  label,
  collapsed,
}: {
  icon: React.ReactNode;
  label: string;
  collapsed?: boolean;
}) {
  return (
    <div
      aria-disabled="true"
      title={`${label} — Próximamente`}
      className={`flex cursor-not-allowed items-center gap-2 rounded px-3 py-2 text-fg-faint ${
        collapsed ? "lg:justify-center lg:px-2" : ""
      }`}
    >
      {icon}
      <span className={`flex-1 ${collapsed ? "lg:hidden" : ""}`}>{label}</span>
      <span className={`rounded-full bg-surface-2 px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wide text-fg-faint ${collapsed ? "lg:hidden" : ""}`}>
        Pronto
      </span>
    </div>
  );
}

interface PendientesSector {
  codigo: string;
  label: string;
  n: number;
}

/**
 * Campana del header con desglose de solicitudes en proceso por sector.
 * No hay sistema de "notificaciones leídas/no leídas" persistido; el
 * dropdown solo refleja conteos vivos del flujo y navega a la lista
 * filtrada del sector elegido.
 */
function CampanaPendientes({
  enabled,
  total,
  porSector,
  onIr,
}: {
  enabled: boolean;
  total: number;
  porSector: PendientesSector[];
  onIr: (codigo: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open]);

  const items = porSector.filter((s) => s.n > 0);

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        className="relative rounded p-2 hover:bg-white/10 disabled:cursor-default disabled:opacity-60"
        aria-label={total > 0
          ? `Notificaciones — ${total} solicitud${total === 1 ? "" : "es"} en proceso`
          : "Notificaciones"}
        title={total > 0
          ? `${total} solicitud${total === 1 ? "" : "es"} en proceso — clic para ver el desglose`
          : "Sin solicitudes en proceso"}
        disabled={!enabled}
        onClick={() => enabled && setOpen((v) => !v)}
      >
        <Bell className="h-5 w-5" />
        {enabled && total > 0 && (
          <span className="absolute -right-0.5 -top-0.5 grid min-h-[18px] min-w-[18px] place-items-center rounded-full bg-amber-500 px-1 text-[10px] font-bold leading-none text-white ring-2 ring-npmx-700 dark:ring-npmx-950">
            {total > 99 ? "99+" : total}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 top-full z-50 mt-2 w-72 rounded-md border border-edge bg-surface text-fg shadow-overlay">
          <div className="border-b border-edge px-4 py-2 text-xs font-semibold uppercase tracking-wide text-fg-subtle">
            Solicitudes en proceso
          </div>
          {items.length === 0 ? (
            <div className="px-4 py-4 text-center text-xs text-fg-subtle">
              Sin pendientes en este momento.
            </div>
          ) : (
            <ul className="divide-y divide-edge">
              {items.map((s) => (
                <li key={s.codigo}>
                  <button
                    type="button"
                    onClick={() => {
                      setOpen(false);
                      onIr(s.codigo);
                    }}
                    className="flex w-full cursor-pointer items-center justify-between gap-2 px-4 py-2 text-left text-sm hover:bg-surface-2"
                  >
                    <span className="font-medium text-fg">{s.label}</span>
                    <span className="flex items-center gap-2">
                      <span className="inline-block min-w-[22px] rounded-full bg-estado-warn-bg px-1.5 py-0.5 text-center text-[11px] font-bold text-estado-warn">
                        {s.n}
                      </span>
                      <ChevronRight className="h-4 w-4 text-fg-faint" />
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
